/* @refresh reset */
import {
  useAccounts,
  useCreateAccount,
  useCurrentAccount,
  useInviteUser,
  useSetCurrentAccount,
  useSetDefaultAccount,
  useUpdateAccount,
} from "#hooks/use-accounts"
import { queryKeys } from "#lib/query-keys"
import { triggerCurrentAccountChange } from "#sdk/account-callback"
import type { Account } from "@home-finance-manager/shared/types/accounts"
import { useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

interface AccountContextType {
  accounts: Account[]
  currentAccount: Account | null
  isLoading: boolean
  isReady: boolean
  error: string | null
  setCurrentAccount: (account: Account) => void
  createAccount: (name: string) => Promise<Account | null>
  updateAccount: (id: number, name: string) => Promise<boolean>
  setDefaultAccount: (id: number) => Promise<boolean>
  inviteUser: (accountId: number, email: string, role?: string) => Promise<boolean>
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

interface AccountProviderProps {
  children: ReactNode
}

export function AccountProvider({ children }: AccountProviderProps) {
  const { data: accounts = [], isLoading: accountsLoading, error: accountsError } = useAccounts()
  const { data: currentAccount } = useCurrentAccount()
  const setCurrentAccountMutation = useSetCurrentAccount()
  const createAccountMutation = useCreateAccount()
  const updateAccountMutation = useUpdateAccount()
  const setDefaultAccountMutation = useSetDefaultAccount()
  const inviteUserMutation = useInviteUser()
  const queryClient = useQueryClient()
  const isInitializingRef = useRef(false)
  const justCreatedAccountRef = useRef<number | null>(null)
  const lastInitializedAccountIdRef = useRef<number | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  const applyAccountChange = useCallback(
    (account: Account, invalidateDependentQueries = false) => {
      localStorage.setItem("current_account", JSON.stringify(account))

      triggerCurrentAccountChange(account)

      queryClient.setQueryData(queryKeys.accounts.current(), account)
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.current() })

      setIsInitialized(true)
      lastInitializedAccountIdRef.current = account.id

      if (invalidateDependentQueries) {
        queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
        queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
        queryClient.invalidateQueries({ queryKey: queryKeys.partners.all })
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      }

      setCurrentAccountMutation.mutate(account)
    },
    [queryClient, setCurrentAccountMutation]
  )

  const findAccountToUse = useCallback((accounts: Account[]): Account | null => {
    if (accounts.length === 0) return null
    return accounts.find((account) => account.meta?.pivot_is_default) || accounts[0] || null
  }, [])

  const hasCheckedInitialAccountRef = useRef(false)
  useEffect(() => {
    if (hasCheckedInitialAccountRef.current) return
    hasCheckedInitialAccountRef.current = true

    const storedAccount = localStorage.getItem("current_account")
    if (storedAccount) {
      try {
        const parsed = JSON.parse(storedAccount)
        if (parsed?.id) {
          lastInitializedAccountIdRef.current = parsed.id
          setIsInitialized(true)
        }
      } catch {
        // Invalid stored account, will be handled by initialization effect
      }
    }
  }, [])

  useEffect(() => {
    if (isInitializingRef.current || accountsLoading) {
      if (!accountsLoading && accounts.length === 0) {
        isInitializingRef.current = false
        lastInitializedAccountIdRef.current = null
      }
      return
    }

    if (!accounts.length) return

    if (justCreatedAccountRef.current) {
      const createdAccount = accounts.find((acc) => acc.id === justCreatedAccountRef.current)
      if (createdAccount) {
        lastInitializedAccountIdRef.current = createdAccount.id
        justCreatedAccountRef.current = null
        return
      }
      justCreatedAccountRef.current = null
    }

    if (
      currentAccount &&
      lastInitializedAccountIdRef.current === currentAccount.id &&
      accounts.some((acc) => acc.id === currentAccount.id)
    ) {
      if (!isInitialized) {
        setIsInitialized(true)
      }
      return
    }

    isInitializingRef.current = true

    const savedAccount = localStorage.getItem("current_account")
    let accountToUse: Account | null = null

    if (savedAccount) {
      try {
        const parsedAccount = JSON.parse(savedAccount)
        const foundAccount = accounts.find((account) => account.id === parsedAccount.id)
        if (foundAccount) {
          accountToUse = foundAccount
        } else {
          localStorage.removeItem("current_account")
          accountToUse = findAccountToUse(accounts)
        }
      } catch {
        localStorage.removeItem("current_account")
        accountToUse = findAccountToUse(accounts)
      }
    } else {
      accountToUse = findAccountToUse(accounts)
    }

    if (accountToUse) {
      if (accountToUse.id !== lastInitializedAccountIdRef.current) {
        if (!currentAccount || currentAccount.id !== accountToUse.id) {
          applyAccountChange(accountToUse, false)
        } else {
          setIsInitialized(true)
          lastInitializedAccountIdRef.current = accountToUse.id
        }
      } else {
        setIsInitialized(true)
      }
    } else if (!accountToUse && accounts.length === 0) {
      setIsInitialized(true)
    }

    isInitializingRef.current = false
  }, [accounts, accountsLoading, applyAccountChange, findAccountToUse, currentAccount])

  useEffect(() => {
    if (!accountsLoading && accounts.length > 0 && !currentAccount) {
      isInitializingRef.current = false
      setIsInitialized(false)
    }
  }, [accounts, accountsLoading, currentAccount])

  useEffect(() => {
    if (currentAccount && lastInitializedAccountIdRef.current === currentAccount.id) {
      setIsInitialized(true)
    }
  }, [currentAccount])

  const setCurrentAccount = useCallback(
    (account: Account) => {
      applyAccountChange(account, true)
    },
    [applyAccountChange]
  )

  const createAccount = useCallback(
    async (name: string): Promise<Account | null> => {
      try {
        const account = await createAccountMutation.mutateAsync(name)
        if (account) {
          justCreatedAccountRef.current = account.id

          queryClient.invalidateQueries({ queryKey: queryKeys.accounts.lists() })

          const updatedAccounts = queryClient.getQueryData<Account[]>(queryKeys.accounts.lists())
          const freshAccount = updatedAccounts?.find((acc) => acc.id === account.id) || account

          isInitializingRef.current = false
          setCurrentAccount(freshAccount)

          setTimeout(() => {
            justCreatedAccountRef.current = null
          }, 500)
        }
        return account
      } catch (error) {
        justCreatedAccountRef.current = null
        return null
      }
    },
    [createAccountMutation, queryClient, setCurrentAccount]
  )

  const updateAccount = useCallback(
    async (id: number, name: string): Promise<boolean> => {
      try {
        await updateAccountMutation.mutateAsync({ id, name })
        return true
      } catch {
        return false
      }
    },
    [updateAccountMutation]
  )

  const setDefaultAccount = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        await setDefaultAccountMutation.mutateAsync(id)
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.lists() })
        const updatedAccounts =
          queryClient.getQueryData<Account[]>(queryKeys.accounts.lists()) || accounts
        const newDefault = updatedAccounts.find((acc) => acc.meta?.pivot_is_default)
        if (newDefault) {
          setCurrentAccount(newDefault)
        }
        return true
      } catch {
        return false
      }
    },
    [setDefaultAccountMutation, queryClient, accounts, setCurrentAccount]
  )

  const inviteUser = useCallback(
    async (accountId: number, email: string, role = "member"): Promise<boolean> => {
      try {
        await inviteUserMutation.mutateAsync({ accountId, email, role })
        return true
      } catch {
        return false
      }
    },
    [inviteUserMutation]
  )

  const isLoading = accountsLoading || setCurrentAccountMutation.isPending

  const isReady = useMemo(() => {
    if (accountsLoading) return false
    if (!isInitialized) return false
    return accounts.length > 0
  }, [accountsLoading, isInitialized, accounts.length])

  const error =
    accountsError instanceof Error
      ? accountsError.message
      : accountsError
        ? String(accountsError)
        : null

  const value: AccountContextType = useMemo(() => {
    const safeSetCurrentAccount = setCurrentAccount || (() => {})
    const safeCreateAccount = createAccount || (async () => null)
    const safeUpdateAccount = updateAccount || (async () => false)
    const safeSetDefaultAccount = setDefaultAccount || (async () => false)
    const safeInviteUser = inviteUser || (async () => false)

    const contextValue: AccountContextType = {
      accounts: Array.isArray(accounts) ? accounts : [],
      currentAccount: currentAccount || null,
      isLoading: isLoading ?? false,
      isReady: isReady ?? false,
      error: error || null,
      setCurrentAccount: safeSetCurrentAccount,
      createAccount: safeCreateAccount,
      updateAccount: safeUpdateAccount,
      setDefaultAccount: safeSetDefaultAccount,
      inviteUser: safeInviteUser,
    }
    return contextValue
  }, [
    accounts,
    currentAccount,
    isLoading,
    isReady,
    error,
    setCurrentAccount,
    createAccount,
    updateAccount,
    setDefaultAccount,
    inviteUser,
  ])

  if (!value || typeof value !== "object") {
    throw new Error("AccountContext value is invalid - this should never happen")
  }

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}

const fallbackContextValue: AccountContextType = {
  accounts: [],
  currentAccount: null,
  isLoading: true,
  isReady: false,
  error: null,
  setCurrentAccount: () => {
    console.warn("setCurrentAccount called but AccountProvider is not available")
  },
  createAccount: async () => {
    console.warn("createAccount called but AccountProvider is not available")
    return null
  },
  updateAccount: async () => {
    console.warn("updateAccount called but AccountProvider is not available")
    return false
  },
  setDefaultAccount: async () => {
    console.warn("setDefaultAccount called but AccountProvider is not available")
    return false
  },
  inviteUser: async () => {
    console.warn("inviteUser called but AccountProvider is not available")
    return false
  },
}

export function useAccount(): AccountContextType {
  const context = useContext(AccountContext)

  if (context === undefined) {
    const isLikelyDevelopment =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")

    if (isLikelyDevelopment) {
      console.warn(
        "useAccount: Context temporarily unavailable (likely HMR). Using fallback values.",
        {
          context,
          providerExists: typeof AccountContext !== "undefined",
        }
      )
      return fallbackContextValue
    }

    const error = new Error("useAccount must be used within an AccountProvider")
    console.error("useAccount called outside AccountProvider", {
      context,
      stack: error.stack,
      providerExists: typeof AccountContext !== "undefined",
    })
    throw error
  }

  return context
}
